import { call, ForkEffect, put, takeEvery, select } from 'redux-saga/effects';
import { combineReducers, Reducer } from 'redux';

import { API, CreateModuleOptions } from './types';
import apiCaller from './ApiCaller';

import { StateManager, combineManagers } from './index';

import {
  PING_INTERVAL,
  PONG_TIMEOUT,
  PING,
  PONG,
  SOCKET_STATES,
  SOCKET_OPENED,
} from './constants';
import Cookies from 'js-cookie';
import { DOMAIN, ENVIRONMENT } from 'settings';
import { getValidAccessToken } from 'services/Api/refreshHelper';
import { getLogoutMarker, rejectBrowserLogin } from 'services/logoutSync';
import { currentLogin, PRESENCE_CLOSED } from 'services/loginProtocol';
import { sessionPresenceChanged } from 'services/sessionPresence';
import { logout } from 'state';
import { useDispatch } from 'react-redux';
import { getStore } from 'src/state/store';

type ConstructorProps = {
  socketUrl?: string;
  managers: StateManager[];
};

export default class GlobalManager {
  readonly sockets: {
    [key: string]: {
      socket: WebSocket;
      uri: string;
      token: string;
      reconnectAttempts: number;
    };
  };
  readonly timeouts: {
    [key: string]: NodeJS.Timeout;
  };
  private pingIntervals: Record<string, NodeJS.Timeout> = {};
  private reconnectTimers: Record<string, NodeJS.Timeout> = {};
  private generations: Record<string, number> = {};
  readonly logging: boolean;

  private handleMessage: (...args: unknown[]) => void;
  private handleOpen: (...args: unknown[]) => void;
  private handleClose: (...args: unknown[]) => void;
  private handleReconnect: (...args: unknown[]) => void;

  readonly socketUrl: string;

  readonly reducer: Reducer;
  readonly saga: () => Generator;
  readonly socketEvents: Record<string, any>;

  constructor(props: ConstructorProps) {
    this.sockets = {};
    this.timeouts = {};
    this.logging = false;

    // Callbacks
    this.handleMessage = () => {
      /*empty*/
    };
    this.handleOpen = () => {
      /*empty*/
    };
    this.handleClose = () => {
      /*empty*/
    };
    this.handleReconnect = () => {
      /*empty*/
    };

    this.socketUrl = props.socketUrl || '';
    const { reducer, saga, socketEvents } = combineManagers(props.managers);

    this.reducer = reducer;
    this.saga = saga;
    this.socketEvents = socketEvents;
  }

  private _ping = (socketDesc: string, socket: WebSocket) => {
    if (this.sockets[socketDesc]?.socket !== socket || socket.readyState !== 1) return;
    socket.send(PING);

    this.timeouts[socketDesc] = setTimeout(() => {
      if (this.sockets[socketDesc]?.socket === socket)
        socket.close(4008, 'ping_timeout');
    }, PONG_TIMEOUT);
  };

  private _onPong = (socketDesc: string) => {
    clearTimeout(this.timeouts[socketDesc]);
  };

  private _listen = (socketDesc: string) => {
    const { socket } = this.sockets[socketDesc];

    socket.onopen = event => {
      if (this.sockets[socketDesc]?.socket !== socket) return;
      clearInterval(this.pingIntervals[socketDesc]);
      this.pingIntervals[socketDesc] = setInterval(
        () => this._ping(socketDesc, socket),
        PING_INTERVAL
      );
      this._log(event, socketDesc);
      this.handleOpen(socketDesc);

      this.sockets[socketDesc].reconnectAttempts = 0;
    };

    socket.onclose = event => {
      if (this.sockets[socketDesc]?.socket !== socket) return;
      clearInterval(this.pingIntervals[socketDesc]);
      clearTimeout(this.timeouts[socketDesc]);
      clearTimeout(this.reconnectTimers[socketDesc]);
      if (event.code === PRESENCE_CLOSED) {
        rejectBrowserLogin(this.sockets[socketDesc].token);
        this.handleClose(socketDesc, event.wasClean);
        return;
      }
      // console.log(this.sockets);
      // attempt to reconnect if socket connection is dropped

      if (!event.wasClean && this.sockets[socketDesc].reconnectAttempts++ < 5) {
        const { token, uri } = this.sockets[socketDesc];
        this.reconnectTimers[socketDesc] = setTimeout(() => {
          if (this.sockets[socketDesc]?.socket === socket)
            void this.connectToSocket(socketDesc, token, uri);
        }, 10000);
      }

      this.handleClose(socketDesc, event.wasClean);
      this._log(event, socketDesc);
    };

    socket.onmessage = event => {
      if (this.sockets[socketDesc]?.socket !== socket) return;
      const message = event.data;
      if (message === PONG) this._onPong(socketDesc);
      else {
        // const parsedData = JSON.parse(event.data);
        // if (parsedData.type === 'send_price') {
        //   console.log('parsedData: ', parsedData);
        //   console.log('typeof parsedData: ', typeof parsedData);
        //   console.log('parsedData.type: ', parsedData.type);
        //   console.log('typeof parsedData.type: ', typeof parsedData.type);
        //   console.log('socketDesc: ', socketDesc);
        // }
        const parsed = JSON.parse(event.data);
        if (parsed.type === 'presence_changed') {
          if (this.sockets[socketDesc]?.socket === socket)
            sessionPresenceChanged(parsed.data?.session_id);
          return;
        }
        this.handleMessage(socketDesc, parsed);
        this._log(event, socketDesc);
      }
    };
  };

  private _log = (event: any, socketDesc: string) => {
    // this.logging &&
    //   console.log({
    //     event,
    //     state: this.getState(socketDesc),
    //     socketObj: this.sockets,
    //   });
  };

  public getState = (socketDesc: string) =>
    SOCKET_STATES[this.sockets[socketDesc]?.socket.readyState ?? 3];

  public connectToSocket = (socketDesc: string, token: string, uri: string) => {
    if (
      this.sockets[socketDesc] &&
      SOCKET_STATES[this.sockets[socketDesc].socket.readyState] ===
        SOCKET_OPENED
    )
      return;
    // Invalidate pending token requests and close superseded connecting sockets.
    this.disconnectFromSocket(socketDesc, false);
    const generation = this.generations[socketDesc];
    // console.log('USED TOKEN TO INITIATE WS CONNECTION: ');
    // console.log(token);
    // const current_token = Cookies.get(`pm_${ENVIRONMENT}_token`);
    // const current_token = await getValidAccessToken();
    console.log('attempting to get token');
    const logoutMarker = getLogoutMarker();
    const refreshCookie = `pm_${ENVIRONMENT}_refresh`;
    const refresh = Cookies.get(refreshCookie);
    const isCurrentSession = () =>
      this.generations[socketDesc] === generation &&
      getLogoutMarker() === logoutMarker &&
      Cookies.get(refreshCookie) === refresh;
    return getValidAccessToken()
      .then(current_token => {
        if (!isCurrentSession() || !current_token) {
          // failed to refresh
          return;
        }
        token = current_token;
        const socket = new WebSocket(`${this.socketUrl}${uri}?token=${token}&presence=1`);
        this.sockets[socketDesc] = { token, uri, socket, reconnectAttempts: 0 };
        this._listen(socketDesc);
      })
      .catch(err => {
        const currentRefresh = Cookies.get(refreshCookie);
        if (
          this.generations[socketDesc] !== generation ||
          getLogoutMarker() !== logoutMarker ||
          (currentRefresh !== undefined && currentRefresh !== refresh)
        )
          return;
        // A temporary auth/Redis/network failure is not an ended managed login.
        // Definite rejection already clears credentials through shared logout.
        if (currentLogin(Cookies.get(`pm_${ENVIRONMENT}_token`))) return;
        // Ordinary refresh failure may already have removed its auth cookies.
        console.error("couldn't refresh token, socket connect aborted", err);
        // logout();
        // const dispatch = useDispatch();
        // dispatch(logout({}));
        // const store = getStore();
        // store.dispatch(logout({}));
        Cookies.remove(`pm_${ENVIRONMENT}_token`, { domain: `${DOMAIN}` });
        Cookies.remove(`pm_${ENVIRONMENT}_token_exp`, { domain: `${DOMAIN}` });
        Cookies.remove(`pm_${ENVIRONMENT}_refresh`, { domain: `${DOMAIN}` });
        Cookies.remove(`pm_${ENVIRONMENT}_refresh_exp`, {
          domain: `${DOMAIN}`,
        });
        Cookies.remove(`pm_${ENVIRONMENT}_soundEnabled`, {
          domain: `${DOMAIN}`,
        });
        Cookies.remove(`pm_${ENVIRONMENT}_person`, { domain: `${DOMAIN}` });
        window.location.reload();
      });

    // if (current_token != undefined) {
    //   token = current_token;
    // } else {
    //   return;
    // }
    // try {
    //   const socket = new WebSocket(`${this.socketUrl}${uri}?token=${token}`);
    //   // console.log('socket: ', socket);
    //   this.sockets[socketDesc] = { token, uri, socket, reconnectAttempts: 0 };
    //   this._listen(socketDesc);
    // } catch (e) {
    //   console.log('socket connection error: ', e);
    // }
  };

  public disconnectFromSocket = (socketDesc: string, notifyClose = true) => {
    this.generations[socketDesc] = (this.generations[socketDesc] || 0) + 1;
    clearInterval(this.pingIntervals[socketDesc]);
    clearTimeout(this.timeouts[socketDesc]);
    clearTimeout(this.reconnectTimers[socketDesc]);
    delete this.pingIntervals[socketDesc];
    delete this.timeouts[socketDesc];
    delete this.reconnectTimers[socketDesc];
    const previous = this.sockets[socketDesc];
    delete this.sockets[socketDesc];
    previous?.socket.close();
    if (previous && notifyClose) this.handleClose(socketDesc, true);
  };

  public onOpen = (func: any) => {
    this.handleOpen = func;
  };

  public onReconnect = (func: any) => {
    this.handleReconnect = func;
  };

  public onMessage = (func: any) => {
    this.handleMessage = func;
  };

  public onClose = (func: any) => {
    this.handleClose = func;
  };
}
